import type { ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth';

interface RedirectIfAuthedProps {
  children: ReactNode;
  to?: string;
}

interface LocationState {
  from?: string;
}

/** Only follow internal absolute paths — blocks open-redirects (`//evil.com`). */
function safeInternal(
  path: string | null | undefined,
  fallback: string,
): string {
  return path && path.startsWith('/') && !path.startsWith('//')
    ? path
    : fallback;
}

/**
 * Inverse of ProtectedRoute — used on /login so already-signed-in users skip
 * the form. Honors the intended destination (router `state.from`, set by
 * ProtectedRoute, or a `?next=` param) before falling back to the dashboard,
 * so deep links land where the user meant to go.
 */
export function RedirectIfAuthed({
  children,
  to = '/dashboard',
}: RedirectIfAuthedProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isBootstrapping = useAuthStore((s) => s.isBootstrapping);
  const location = useLocation();
  const [params] = useSearchParams();

  if (isBootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink-muted">
        Loading…
      </div>
    );
  }

  if (isAuthenticated) {
    const fromState = (location.state as LocationState | null)?.from;
    const target = safeInternal(fromState ?? params.get('next'), to);
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
